<template>
  <div class="fill-height of-y d-flex flex-column" style="overflow-x: hidden">
    <div v-if="startingUp" class="flex-grow-1 d-flex flex-column align-center justify-center ">
      <v-progress-circular
        :size="50"
        color="secondary"
        indeterminate
      ></v-progress-circular>
      <h3 class="pt-4">Fetching Classifications</h3>
    </div>

    <div v-else class="flex-grow-1 of-y">
      <v-virtual-scroll
        :items="filteredClassifications"
        :item-height="50"
        class="fill-height"
        @scroll="showMenu = false"
      >
        <template v-slot:default="{ item }">
          <app-classification-item
            :key="item.match"
            :cls="item"
            :loading="loadingMatches.includes(item.match)"
            :selected="selectedMatches.includes(item.match)"
            :manual-time-tab="isManualTimeTab"
            :show-more="!selectedMatches || selectedMatches.length === 0"
            @dropdownClicked="dropdownClicked($event, item)"
            @moreClicked="selectedStatusMap = $event; showMore = true;"
            @editClicked="mtCreateObj = {manualTimeTeamIds: [], ...$event}; mtCreateOriginalObj = {...$event}; $store.state.ref.classification.showCreate = true"
            @rowClicked="access.statistics ? (selectedMatches.length ? selectedClicked(item, !selectedMatches.includes(item.match)) : gotoAppStats($event.match)) : () => {}"
            @selected="selectedClicked(item, $event)"
          >
          </app-classification-item>
        </template>
      </v-virtual-scroll>
    </div>

    <v-menu
      v-if="showMenu"
      v-model="showMenu"
      :position-x="x"
      :position-y="y"
      absolute
      offset-y
      :max-width="width"
      :min-width="width"
    >
      <v-card>
        <v-list dense>
          <v-list-item-group
            :value="selectedStatusMap.defaultLabel"
            color="primary"
          >
            <v-list-item
              v-for="label in labels"
              :value="label.id"
              :key="label.id"
              @click="changeLabel(label.id)"
              active-color="primary"
            >
              <v-list-item-content>
                <v-list-item-title v-text="label.text"></v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-card>
    </v-menu>

    <app-classification-dlg
      ref="classificationDlg"
      @future="saveLabelChangesConfirm()"
      @history="saveLabelChangesConfirm(true)"
    />

    <app-classification-item-options-dlg
      :shown.sync="showMore"
      :status-map="selectedStatusMap"
      @input="changesSavedClassificationItem"
    ></app-classification-item-options-dlg>

    <app-dlg
      v-if="$store.state.ref.classification.showCreate"
      :show-dlg.sync="$store.state.ref.classification.showCreate"
      :title="`${mtCreateObj._id ? 'Edit' : 'Create'} Manual Time Option`"
      :default-padding="false"
      :dlg-width="750"
    >

      <template>
        <v-text-field
          ref="manualTimeLabel"
          v-model="mtCreateObj.match"
          label="Option Description"
          item-text="Option name"
          return-object
          prepend-icon="mdi-format-color-text"
          class="mx-3"
          :rules="[validationRules.required, duplicateManualTimeRule]"
        ></v-text-field>

        <v-autocomplete
          v-model="mtCreateObj.defaultLabel"
          label="Label"
          :items="labels.filter(label => label.id === 1 || label.id === 2 || label.id === 0)"
          item-text="text"
          item-value="id"
          prepend-icon="mdi-tag"
          class="mx-3"
        ></v-autocomplete>

        <v-text-field
          ref="manualTimeMaxTimeLimit"
          class="mx-3"
          type="number"
          return-object
          label="Max Time Duration (Seconds)"
          item-text="Option name"
          prepend-icon="mdi-timer-stop"
          v-model="mtCreateObj.manualTimeMaxTimeLimit"
        ></v-text-field>

        <div class="px-6 d-flex align-center justify-space-between flex-wrap">
          <div style="min-width: 350px" class="flex-equal flex-grow-1">
            <v-switch
              label="Auto Approve"
              hint="Manual Time will be auto approved, once logged"
              persistent-hint
              color="primary"
              class="pt-0 mt-0 pb-5"
              v-model="mtCreateObj.manualTimeAutoApprove"
            ></v-switch>
          </div>
          <div style="min-width: 350px" class="flex-equal flex-grow-1">
            <v-switch
              label="Motivation Required"
              hint="Employee will need to provide a motivation for logging this manual time"
              persistent-hint
              color="primary"
              class="pt-0 mt-0 pb-3"
              v-model="mtCreateObj.manualTimeMotivation"
            ></v-switch>
          </div>
        </div>

        <v-autocomplete
          v-model="mtCreateObj.manualTimeTeamIds"
          label="Limit to Teams"
          :items="teams"
          item-text="name"
          item-value="_id"
          multiple
          chips
          deletable-chips
          small-chips
          clearable
          prepend-icon="mdi-account-group"
          class="mx-3 pb-4"
          placeholder="Available to all teams"
          persistent-placeholder
          hint="Leave empty to make this option available to all teams. Otherwise, only employees in the selected teams can see and log it."
          persistent-hint
        ></v-autocomplete>

      </template>

      <template v-slot:actions>
        <v-btn
          v-if="mtCreateObj._id"
          @click="removeManualTime()"
          :disabled="loadingManualTime"
          :loading="loadingManualTime"
          small
          class="ml-2"
          color="error"
          variant="contained"
          size="small"
        >Remove Option</v-btn>
        <v-btn
          @click="$store.state.ref.classification.showCreate = false"
          :disabled="loadingManualTime"
          small
          class="ml-2"
          color="blue"
          outlined
          size="small"
        >Cancel</v-btn>
        <v-btn
          @click="upsertManualTime()"
          :disabled="
          !mtCreateObj.match ||
          duplicateManualTimeRule(mtCreateObj.match) !== true ||
          loadingManualTime"
          :loading="loadingManualTime"
          small
          class="ml-2"
          color="secondary"
          variant="contained"
          size="small"
        >{{ mtCreateObj._id ? "Edit" : "Create" }}</v-btn>
      </template>
    </app-dlg>

  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import {StatusMap} from "@/models/statusMap";
import AppClassificationItem from "@/components/Classification/AppClassificationItem";
import {arrSplice, validationRules} from "@/core/codeFunctions";
import AppDlg from "@/components/Shared/AppDlg";
import AppDivider from "@/components/Shared/AppDivider/AppDivider";
import AppClassificationItemOptionsDlg from "@/components/Classification/AppClassificationItemOptionsDlg";
import AppClassificationDlg from "@/components/Shared/AppClassificationDlg";
import store from "@/core/store";

export default {
  components: {AppClassificationDlg, AppClassificationItemOptionsDlg, AppDivider, AppDlg, AppClassificationItem},
  props: {
    shown: Boolean
  },
  data() {
    return {
      console,

      startingUp: true,
      rawClassifications: [],
      showMenu: false,
      x: 0,
      y: 0,
      width: 0,
      selectedStatusMap: null,
      loadingMatches: [],
      showChangeLabel: false,
      changeLabelId: null,
      showMore: false,
      validationRules: validationRules,
      editStatusMap: new StatusMap({}),
      savingStatusMapAlias: false,

      selectedMatches: [],
      arrSplice,
      ctrlDown: false,
      shiftDown: false,
      mtCreateObj: {
        defaultLabel: 0,
        match: null,
        _id: undefined,
        manualTimeMaxTimeLimit: null,
        manualTimeAutoApprove: null,
        manualTimeMotivation: null,
        manualTimeTeamIds: [],
      },
      mtCreateOriginalObj: null,
      loadingManualTime: false
    }
  },
  watch: {

    "this.$store.state.ref.classification.tabIndex": {
      immediate: true,
      handler(val) {
        arrSplice(this.selectedMatches, () => true)
      }
    },

    "$store.state.ref.classification.showCreate": {
      handler(val) {
        if (!val) {
          this.mtCreateObj = {
            defaultLabel: 0,
            match: null,
            _id: undefined,
            manualTimeMaxTimeLimit: null,
            manualTimeAutoApprove: null,
            manualTimeMotivation: null,
            manualTimeTeamIds: [],
          }
        } else {
          setTimeout(() => {
            this.$refs.manualTimeLabel.focus();
          }, 500)
        }
      }
    }

  },
  mounted() {
    this.fetchClassifications();
    window.addEventListener('keyup', this.addListenerUp);
    window.addEventListener('keydown', this.addListenerDown);
  },
  beforeDestroy() {
    window.removeEventListener('keyup', this.addListenerUp);
    window.removeEventListener('keydown', this.addListenerDown);
  },

  methods: {

    async removeManualTime() {
      const vm = this;
      const label = new StatusMap({
        ...this.mtCreateObj,
        manualTime: true
      });
      try {
        this.loadingManualTime = true
        vm.loadingMatches.push(label.match)
        const {_id} = await label.remove();
        await this.$store.state.statusmaps.once("deleted")
        arrSplice(vm.loadingMatches, val => val === label.match)
        this.$store.state.ref.classification.showCreate = false
      } catch (e) {
        vm.$snack.netWorkError()
        console.log(e)
        arrSplice(vm.loadingMatches, val => val === label.match)
      } finally {
        this.loadingManualTime = false
      }
    },

    async upsertManualTime() {
      if (this.duplicateManualTimeRule(this.mtCreateObj.match) !== true)
        return this.$snack.error('Manual Time already exists');

      this.mtCreateObj.manualTimeMaxTimeLimit = parseInt(this.mtCreateObj.manualTimeMaxTimeLimit);

      const vm = this;
      const label = new StatusMap({
        ...this.mtCreateObj,
        manualTime: true
      });
      try {
        this.loadingManualTime = true;
        vm.loadingMatches.push(label.match);
        const {_id} = await label.upsert(false);
        await vm.statusmaps.once(_id);
        arrSplice(vm.loadingMatches, val => val === label.match);
        this.$store.state.ref.classification.showCreate = false;
      } catch (e) {
        vm.$snack.netWorkError();
        console.log(e);
        arrSplice(vm.loadingMatches, val => val === label.match);
      } finally {
        this.loadingManualTime = false;
      }
    },

    duplicateManualTimeRule(val) {
      let compare = (val || '').toLowerCase();
      if (
        (this.mtCreateOriginalObj?.match.toLowerCase() !== compare) &&
        this.tabbedClassifications.find(classification => classification.match.toLowerCase() === compare)
      ) {
        return `${val} already exists`
      } else
        return true
    },

    selectedClicked(cls, select) {
      if (select) {
        this.selectedMatches.push(cls.match)
      } else {
        arrSplice(this.selectedMatches, match => cls.match === match)
      }

      if (this.shiftDown && select) {
        // Get First Index
        const toIndex = this.filteredClassifications.reduce((val, item, index) => {
          if (item.match === cls.match)
            val = index
          return val
        }, null)

        const fromIndex = this.selectedMatches.reduce((val, item) => {
          const index = this.filteredClassifications.findIndex(cls => cls.match === item);
          if (!val || index < val)
            return  index
          return val
        }, null)

        arrSplice(this.selectedMatches, () => true)

        this.filteredClassifications.forEach((val, index) => {
          if (index >= fromIndex && index <= toIndex)
            this.selectedMatches.push(val.match)
        })
      }
    },

    addListenerUp(e) {
      switch (e.key) {
        case 'Shift' : return this.shiftDown = false
        case 'Control' : return this.ctrlDown = false
      }
    },
    addListenerDown(e) {
      switch (e.key) {
        case 'Shift' : return this.shiftDown = true
        case 'Control' : return this.ctrlDown = true
      }
    },
    ...mapActions(["gotoPage", "gotoAppStats"]),
    async saveLabelChangesConfirm(updateHistorical = false) {
      const vm = this;

      async function updateLabel(statusMap) {
        const label = new StatusMap({
          ...JSON.parse(JSON.stringify(statusMap)),
          defaultLabel: vm.changeLabelId
        });
        try {
          vm.loadingMatches.push(label.match)
          const {_id} = await label.upsert(updateHistorical);
          await vm.statusmaps.once(_id)
          arrSplice(vm.loadingMatches, val => val === label.match)

        } catch (e) {
          vm.$snack.netWorkError()
          console.log(e)
          arrSplice(vm.loadingMatches, val => val === label.match)
        }
      }

      const selectedMatches = [...this.selectedMatches]
      arrSplice(this.selectedMatches, () => true)

      if (selectedMatches.length) {
        for (const matchStr of selectedMatches) {
          // Get The Status Map
          const map = this.filteredClassifications.find(cls => cls.match === matchStr)
          updateLabel(map).catch(e => console.log(e))
        }
      } else {
        updateLabel(this.selectedStatusMap).catch(e => console.log(e))
      }
    },

    changesSavedClassificationItem(statusMap) {
      this.selectedStatusMap = statusMap;
      this.showMore = false;
    },

    changeLabel(labelId) {
      this.changeLabelId = labelId;

      if (this.$store.state.historyPreference)
        this.saveLabelChangesConfirm(this.$store.state.historyPreference === 'history')
      else {
        this.$refs.classificationDlg.showDlg()
        this.showChangeLabel = true;
      }
    },

    dropdownClicked(e, item) {

      if (!this.selectedMatches.includes(item.match))
        arrSplice(this.selectedMatches, () => true)

      this.selectedStatusMap = e.value;
      const params = e.component.$el.getBoundingClientRect();
      this.showMenu = false
      this.$nextTick(() => {
        this.x = params.left
        this.y = params.bottom
        this.width = params.width
        this.showMenu = true
      })
    },

    async fetchClassifications() {
      try {
        this.startingUp = true;
        this.rawClassifications = await this.$newReq('get', 'classification/getAll');
        this.startingUp = false;
        this.openApp()
      } catch (e) {
        this.$snack.netWorkError()
        this.gotoPage('dashboard')
      }
    },

    openApp() {
      if (this.openOnStartup) {
        const cls = this.filteredClassifications.find(app => app.match === this.openOnStartup)

        if (cls) {
          this.selectedStatusMap = cls;
          this.showMore = true;
        }

        this.openOnStartup = null;
      }
    }
  },
  computed: {
    ...mapGetters(['statusmaps', 'statusmapsByMatch', 'labels', 'access', 'teams']),

    openOnStartup: {
      get() {
        return this.$store.state.ref.classification.openOnStartup
      },
      set(val) {
        this.$store.state.ref.classification.openOnStartup = val
      }
    },

    mappedClassifications() {
      const addedClassifications = []
      const classifications = this.rawClassifications.map((match) => {
        addedClassifications.push(match.match)
        return new StatusMap((() => {
          const sm = this.statusmapsByMatch[match.match]
          if (sm)
            return new StatusMap({...sm, totalDuration: match.totalDuration})
          return new StatusMap(match)
        })())
      })
      return [
        ...classifications,
        ...this.statusmaps.filter(sMap => !addedClassifications.includes(sMap.match))
      ]
        .sort((a,b) => (b.totalDuration || 0)-(a.totalDuration || 0));

    },

    tabbedClassifications() {
      const tab = this.$store.state.ref.classification.tabIndex;

      // Check if company has AI Classification enabled or not
      const aiClassificationsEnabled = store.getters.getSiteConfig;

      if (tab === 1)
        return this.mappedClassifications.filter(cls => !!cls._id)

      // AI Classified
      if (tab === 2) {
        if (aiClassificationsEnabled) {
          return this.mappedClassifications.filter(item => item.aiAssigned);
        } else {
          return this.mappedClassifications.filter(cls => !cls._id);
        }
      }

      if (tab === 3) {
        if (aiClassificationsEnabled) {
          return this.mappedClassifications.filter(cls => !cls._id)
        } else {
          return this.mappedClassifications.filter(cls => cls.manualTime)
        }
      }

      if (tab === 4 && aiClassificationsEnabled) {
        return this.mappedClassifications.filter(cls => cls.manualTime)
      }

      return this.mappedClassifications;
    },

    isManualTimeTab() {
      const tab = this.$store.state.ref.classification.tabIndex;
      const aiClassificationsEnabled = store.getters.getSiteConfig;
      return aiClassificationsEnabled ? tab === 4 : tab === 3;
    },

    filteredClassifications() {
      if (this.$store.state.ref.classification.filterStr)
        return this.tabbedClassifications.filter(cls => `${cls.match}${cls.alias || ''}`.toLowerCase()
          .includes((this.$store.state.ref.classification.filterStr || '').toLowerCase()));
      else
        return this.tabbedClassifications;
    },
  },
}
</script>

<style scoped>

</style>